import React from 'react';
import { Body5, Body7, Container } from '@ui';
import { SkeletonButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';
import { fetchRunStaticInfo, TEMPLATE_RUN_STATUSES } from '@services/fmdc';
import { useFmdcPoll } from './useFmdcPoll';
import { ConfidenceDot } from './ConfidenceDot';
import { StatusPill } from './StatusPill';
import { HeaderCell, HeaderRow, Mono, Row, Table, TableScroll } from './styles';
import { formatCount, humanizeToken, EM_DASH } from './format';

// The Static Info tab: the model's metadata fields. The answers arrive about 2–3 minutes after the
// run finishes, so the tab polls while the template run is still going and says so.

// Field | Value | Confidence | Source | Status
const GRID_COLUMNS =
  'minmax(180px, 1.6fr) minmax(200px, 2.2fr) minmax(90px, 0.8fr) minmax(130px, 1.1fr) minmax(110px, 0.9fr)';

const FIELD_STATUS_TOKENS = {
  answered: 'positive',
  deterministic: 'positive',
  pending: 'primaryCtaButton',
  missing: 'secondaryText',
};

const ANSWERED_STATUSES = ['answered', 'deterministic'];

const hasValue = (value) => value !== null && value !== undefined && value !== '';

export const RunStaticInfo = ({ runId }) => {
  const [templateRun, setTemplateRun] = React.useState(null);
  const [fields, setFields] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);

  const load = React.useCallback(
    async (guard) => {
      const result = await fetchRunStaticInfo(runId);
      if (guard?.isCanceled) return result;
      if (result.error) {
        setError(result.error);
        setFields((prev) => prev || []);
      } else {
        setError(null);
        setTemplateRun(result.templateRun);
        setFields(result.fields);
      }
      return result;
    },
    [runId]
  );

  React.useEffect(() => {
    const guard = { isCanceled: false };
    setError(null);
    load(guard);
    return () => {
      guard.isCanceled = true;
    };
  }, [load, reloadKey]);

  const isAnswering = templateRun?.status === TEMPLATE_RUN_STATUSES.RUNNING;
  useFmdcPoll(load, { enabled: isAnswering });

  if (fields === null && !error) {
    return <Body5 isLowContrast>Loading Static_Info…</Body5>;
  }

  if (error && (!fields || fields.length === 0)) {
    return (
      <div>
        <Body5>{error}</Body5>
        <Container top={12}>
          <SkeletonButton onClick={() => setReloadKey((k) => k + 1)}>Retry</SkeletonButton>
        </Container>
      </div>
    );
  }

  const answered = fields.filter((f) => ANSWERED_STATUSES.includes(f.status) || hasValue(f.value)).length;

  return (
    <div>
      {isAnswering && (
        <Container bottom={12}>
          <Body5 isLowContrast role="status">
            Answering Static_Info fields (about 2 to 3 minutes after the run finishes)…
          </Body5>
        </Container>
      )}
      {templateRun?.status === TEMPLATE_RUN_STATUSES.ERROR && (
        <Container bottom={12}>
          <ErrorText>The Static_Info template run failed. Rerun to try again.</ErrorText>
        </Container>
      )}
      <Container bottom={12}>
        <Body5>
          {formatCount(answered)} of {formatCount(fields.length)} answered
        </Body5>
      </Container>
      {fields.length === 0 ? (
        <Body5 isLowContrast>No Static_Info fields on this template.</Body5>
      ) : (
        <TableScroll>
          <Table>
            <HeaderRow $columns={GRID_COLUMNS}>
              <HeaderCell>Field</HeaderCell>
              <HeaderCell>Value</HeaderCell>
              <HeaderCell>Confidence</HeaderCell>
              <HeaderCell>Source</HeaderCell>
              <HeaderCell>Status</HeaderCell>
            </HeaderRow>
            {fields.map((field) => (
              <Row key={field.field_code || field.label} $columns={GRID_COLUMNS} $interactive={false}>
                <div>
                  <Body5 bold>{field.label || field.field_code}</Body5>
                  {field.field_code && (
                    <Container top={4}>
                      <Mono>{field.field_code}</Mono>
                    </Container>
                  )}
                </div>
                <div>
                  {hasValue(field.value) ? (
                    <Body7>{String(field.value)}</Body7>
                  ) : (
                    <Body7 isLowContrast>{field.status === 'pending' ? 'pending' : 'missing'}</Body7>
                  )}
                </div>
                <ConfidenceDot confidence={field.confidence} />
                <div>
                  {field.source_location ? (
                    <Mono>{field.source_location}</Mono>
                  ) : (
                    <Body7 isLowContrast>{EM_DASH}</Body7>
                  )}
                </div>
                <div>
                  <StatusPill
                    token={FIELD_STATUS_TOKENS[field.status] || 'secondaryText'}
                    label={field.status ? humanizeToken(field.status) : EM_DASH}
                    pulse={field.status === 'pending'}
                  />
                </div>
              </Row>
            ))}
          </Table>
        </TableScroll>
      )}
    </div>
  );
};
